// Shared multi-step wizard container: numbered progress header, back/next
// footer, and optional resume-where-you-left-off via localStorage. Step
// CONTENT and form state belong to the consumer — the shell only owns which
// step is visible, so validation stays live (rebuild the steps array each
// render and compute nextDisabled from current form state).
//
// Usage:
//   <WizardShell
//     accent="red"
//     storageKey={`podcast-show-wizard-${facilityId}`}
//     steps={[
//       { key: "basics", title: "Basics", content: <BasicsFields …/>,
//         nextDisabled: !title.trim() },
//       { key: "voice", title: "Voice & TTS", content: <VoiceFields …/> },
//     ]}
//     onStepChange={(key) => saveDraft()}
//     onFinish={submit}
//     finishLabel="Create show"
//     busy={saving}
//     onCancel={close}
//   />
import { useState } from "react";

// Literal class strings per accent — keeps Tailwind's scanner happy and the
// look consistent with each portal's existing palette.
const ACCENTS = {
  red: {
    button: "bg-red-700 hover:bg-red-800",
    active: "bg-red-700 border-red-700 text-white",
    done: "border-red-700 text-red-700",
  },
  rose: {
    button: "bg-rose-600 hover:bg-rose-700",
    active: "bg-rose-600 border-rose-600 text-white",
    done: "border-rose-600 text-rose-600",
  },
  blue: {
    button: "bg-blue-600 hover:bg-blue-700",
    active: "bg-blue-600 border-blue-600 text-white",
    done: "border-blue-600 text-blue-600",
  },
  purple: {
    button: "bg-purple-600 hover:bg-purple-700",
    active: "bg-purple-600 border-purple-600 text-white",
    done: "border-purple-600 text-purple-600",
  },
  amber: {
    button: "bg-amber-600 hover:bg-amber-700",
    active: "bg-amber-600 border-amber-600 text-white",
    done: "border-amber-600 text-amber-600",
  },
  orange: {
    button: "bg-orange-600 hover:bg-orange-700",
    active: "bg-orange-600 border-orange-600 text-white",
    done: "border-orange-600 text-orange-600",
  },
  cyan: {
    button: "bg-cyan-600 hover:bg-cyan-700",
    active: "bg-cyan-600 border-cyan-600 text-white",
    done: "border-cyan-600 text-cyan-600",
  },
};

const readSavedIndex = (storageKey, stepCount) => {
  if (!storageKey) return 0;
  const saved = Number(localStorage.getItem(`wizard-step-${storageKey}`));
  return Number.isInteger(saved) && saved >= 0 && saved < stepCount ? saved : 0;
};

export function WizardShell({
  steps,
  accent = "red",
  storageKey,
  onStepChange,
  onFinish,
  finishLabel = "Finish",
  busy = false,
  onCancel,
  cancelLabel = "Cancel",
}) {
  const [index, setIndex] = useState(() =>
    readSavedIndex(storageKey, steps.length),
  );
  const colors = ACCENTS[accent] || ACCENTS.red;
  const current = steps[Math.min(index, steps.length - 1)];
  const isLast = index >= steps.length - 1;

  const goTo = (i) => {
    const next = Math.max(0, Math.min(i, steps.length - 1));
    setIndex(next);
    if (storageKey)
      localStorage.setItem(`wizard-step-${storageKey}`, String(next));
    if (onStepChange) onStepChange(steps[next].key);
  };

  const advance = () => {
    if (isLast) {
      if (storageKey) localStorage.removeItem(`wizard-step-${storageKey}`);
      if (onFinish) onFinish();
      return;
    }
    goTo(index + 1);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-y-2">
        {steps.map((s, i) => {
          const state = i < index ? "done" : i === index ? "active" : "todo";
          return (
            <div key={s.key} className="flex items-center">
              {i > 0 && (
                <div
                  className={`w-6 h-px mx-1 ${
                    i <= index ? "bg-slate-400" : "bg-slate-200"
                  }`}
                />
              )}
              <button
                type="button"
                disabled={state !== "done" || busy}
                onClick={() => goTo(i)}
                className={`flex items-center gap-1.5 ${
                  state === "done" ? "cursor-pointer" : "cursor-default"
                }`}
                title={state === "done" ? `Back to ${s.title}` : undefined}
              >
                <span
                  className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs font-medium ${
                    state === "active"
                      ? colors.active
                      : state === "done"
                        ? colors.done
                        : "border-slate-300 text-slate-400"
                  }`}
                >
                  {state === "done" ? "✓" : i + 1}
                </span>
                <span
                  className={`text-sm ${
                    state === "active"
                      ? "text-slate-800 font-medium"
                      : state === "done"
                        ? "text-slate-600"
                        : "text-slate-400"
                  }`}
                >
                  {s.title}
                </span>
              </button>
            </div>
          );
        })}
        <span className="ml-auto text-xs text-slate-400">
          Step {index + 1} of {steps.length}
        </span>
      </div>

      <div>{current.content}</div>

      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
        {index > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => goTo(index - 1)}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm disabled:opacity-50"
          >
            Back
          </button>
        )}
        <div className="flex-1" />
        {onCancel && (
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-slate-500 text-sm disabled:opacity-50"
          >
            {cancelLabel}
          </button>
        )}
        <button
          type="button"
          disabled={busy || current.nextDisabled}
          onClick={advance}
          className={`px-5 py-2 rounded-lg text-white text-sm disabled:opacity-50 ${colors.button}`}
        >
          {busy ? "Working…" : isLast ? finishLabel : "Next"}
        </button>
      </div>
    </div>
  );
}
